import { useApi } from '@/api'
import {
    ActivityStatusV1IdEnum,
    type GetNamedActivitySchemaV1,
    type GetUpcomingVesselActivityV1200Response,
    type GetVesselSmartRoutineV1
} from '@geckoal/gecko-api-client'

export interface SessionActivity {
    activityId: string
    name: string | null
    startAt: Date
    endAt: Date | null
    durationInSeconds: number
    prepDurationInSeconds: number
    statusId: string
    statusReason: string | null
    smartRoutineId: string | null
    setPointC: number | null
}

function mapActivityToSession(activity: GetNamedActivitySchemaV1 | GetUpcomingVesselActivityV1200Response): SessionActivity {
    return {
        activityId: activity.activityId,
        name: (activity.name as unknown as string) || null,
        startAt: new Date(activity.startAt),
        endAt: activity.endAt ? new Date(activity.endAt as unknown as string) : null,
        durationInSeconds: activity.durationInSeconds,
        prepDurationInSeconds: activity.prepDurationInSeconds,
        statusId: activity.status.id,
        statusReason: activity.status.reason || null,
        smartRoutineId: (activity.smartRoutineId as unknown as string) || null,
        setPointC: activity.setPointC ?? null
    }
}

// `statuses` replaces the endpoint's default filtering entirely (admin-only server-side, harmless no-op
// otherwise) rather than adding to it — so this must list every status, not just "done", or the others
// would disappear from the caller's view. Every current caller (session history, the schedules calendar)
// wants DONE sessions included, so there's no partial-status variant of this helper.
const ALL_ACTIVITY_STATUSES = Object.values(ActivityStatusV1IdEnum).join(',')

/**
 * Fetch smart routine / manual sessions (activities) for a vessel from the Cloud API, including DONE ones.
 */
export async function fetchSessionActivities(accountId: number, vesselId: number, options: { from?: Date; to?: Date } = {}): Promise<SessionActivity[]> {
    const response = await useApi().getVesselActivitiesV1({
        accountId,
        vesselId,
        from: options.from,
        to: options.to,
        statuses: ALL_ACTIVITY_STATUSES
    })

    return (response || []).map(mapActivityToSession)
}

/**
 * Fetch the current or next relevant session for a vessel (planned, preparing, or in progress).
 * This can reflect a session before it has been materialized in the activities history list,
 * so it's the source to use when a vessel has no history yet but does have something scheduled.
 */
export async function fetchUpcomingSessionActivity(accountId: number, vesselId: number): Promise<SessionActivity | null> {
    const activity = await useApi()
        .getUpcomingVesselActivityV1({ accountId, vesselId })
        .catch(() => null)

    return activity ? mapActivityToSession(activity) : null
}

export type UpcomingActivityDetail = GetUpcomingVesselActivityV1200Response

/**
 * Same call as fetchUpcomingSessionActivity, but returns the full official response shape (needed by
 * consumers like ActivitySessionDialog that already depend on its other fields, e.g. prepDurationInSeconds).
 * Unlike fetchUpcomingSessionActivity, this does NOT swallow errors — callers that need to surface API
 * failures (rather than treat them the same as "no session") should let this reject and handle it themselves.
 */
export async function fetchUpcomingActivityDetail(accountId: number, vesselId: number): Promise<UpcomingActivityDetail | null> {
    return await useApi().getUpcomingVesselActivityV1({ accountId, vesselId })
}

/**
 * Fetch a single smart routine's name, even if it has since been deleted — unlike fetchSmartRoutines
 * (which only lists currently-existing routines), this resolves a specific smartRoutineId referenced
 * by a session so its name still shows correctly instead of falling back to a generic label.
 */
export async function fetchSmartRoutineName(accountId: number, vesselId: number, smartRoutineId: string): Promise<string | null> {
    const response = await useApi()
        .getVesselSmartRoutinesDetailsV1({ accountId, vesselId, smartRoutineId })
        .catch(() => null)

    return response?.routine.name ?? null
}

/**
 * Fetch the smart routines configured for a vessel (the recurring routine definitions,
 * independent of whether they have produced any session history yet).
 */
export async function fetchSmartRoutines(accountId: number, vesselId: number): Promise<GetVesselSmartRoutineV1[]> {
    const routinesResponse = await useApi()
        .getVesselSmartRoutinesV1({ accountId, vesselId })
        .catch(() => null)

    return routinesResponse?.routines ?? []
}

/**
 * Build a smartRoutineId -> routine name lookup for a vessel.
 */
export async function fetchRoutineNameMap(accountId: number, vesselId: number): Promise<Record<string, string>> {
    const routines = await fetchSmartRoutines(accountId, vesselId)

    const map: Record<string, string> = {}
    for (const routine of routines) {
        map[routine.smartRoutineId] = routine.name
    }
    return map
}

/**
 * Map session status to PrimeVue Tag severity.
 */
export function sessionStatusSeverity(statusId: string): string {
    switch (statusId) {
        case ActivityStatusV1IdEnum.Idle:
            return 'contrast'
        case ActivityStatusV1IdEnum.Preparing:
        case ActivityStatusV1IdEnum.Ready:
            return 'info'
        case ActivityStatusV1IdEnum.InProgress:
            return 'success'
        case ActivityStatusV1IdEnum.InGracePeriod:
            return 'warn'
        case ActivityStatusV1IdEnum.Done:
            return 'secondary'
        default:
            return 'secondary'
    }
}

/**
 * Map session status to a display label.
 */
export function sessionStatusLabel(statusId: string): string {
    switch (statusId) {
        case ActivityStatusV1IdEnum.InProgress:
            return 'In Progress'
        case ActivityStatusV1IdEnum.Preparing:
            return 'Preparing'
        case ActivityStatusV1IdEnum.Ready:
            return 'Ready'
        case ActivityStatusV1IdEnum.Done:
            return 'Done'
        case ActivityStatusV1IdEnum.InGracePeriod:
            return 'Grace Period'
        case ActivityStatusV1IdEnum.Idle:
            return 'Planned'
        default:
            return statusId
    }
}

/**
 * Map session status to a Vuetify chip color (same severities as sessionStatusSeverity, different palette).
 */
export function sessionStatusColor(statusId: string): string {
    switch (statusId) {
        case ActivityStatusV1IdEnum.Idle:
            return 'blue-grey'
        case ActivityStatusV1IdEnum.Preparing:
        case ActivityStatusV1IdEnum.Ready:
            return 'blue'
        case ActivityStatusV1IdEnum.InProgress:
            return 'green'
        case ActivityStatusV1IdEnum.InGracePeriod:
            return 'orange'
        case ActivityStatusV1IdEnum.Done:
            return 'grey-darken-4'
        default:
            return 'grey'
    }
}

/**
 * The moment this session actually began, for display purposes. Normally that's startAt (the scheduled
 * *real* start, after prep). But if the session was cancelled/failed during prep — before ever reaching
 * that real start — startAt is a future timestamp that was never reached, and using it as "Start" would
 * show a start time after the session's actual end. In that case we show when prep began instead
 * (startAt - prepDurationInSeconds), which always precedes the actual end.
 */
export function effectiveStartAt(session: SessionActivity): Date {
    if (session.endAt && session.endAt.getTime() < session.startAt.getTime()) {
        return new Date(session.startAt.getTime() - session.prepDurationInSeconds * 1000)
    }
    return session.startAt
}

/**
 * The session's real elapsed duration in seconds, rather than its planned/target one — measured from
 * effectiveStartAt (see above) so it stays consistent with what's shown as "Start". Sessions with no
 * endAt yet (planned, preparing, in progress) fall back to the planned duration, since nothing "actual"
 * exists yet.
 */
export function actualDurationInSeconds(session: SessionActivity): number {
    if (!session.endAt) return session.durationInSeconds
    return Math.max(0, (session.endAt.getTime() - effectiveStartAt(session).getTime()) / 1000)
}

/**
 * Format duration in seconds to a human-readable string.
 */
export function formatDuration(seconds: number): string {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.round((seconds % 3600) / 60)
    if (hours > 0) {
        return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`
    }
    return `${minutes}m`
}

/**
 * Format a date for display, optionally in a specific IANA timezone.
 */
export function formatSessionDateTime(date: Date, timezone?: string): string {
    return new Intl.DateTimeFormat('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: timezone
    }).format(new Date(date))
}
